import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';

export const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        try {
            await authAPI.forgotPassword(email);
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to send reset email');
        } finally {
            setLoading(false);
        }
    };

    // Email sent confirmation
    if (sent) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex" data-testid="forgot-password-page">
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="w-full max-w-md">
                        <Link 
                            to="/" 
                            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </Link>

                        <Card className="border-border/60">
                            <CardHeader className="space-y-1 text-center">
                                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="h-8 w-8 text-green-600" />
                                </div>
                                <CardTitle className="font-serif text-2xl">Check your email</CardTitle>
                                <CardDescription>
                                    If an account exists for <strong>{email}</strong>, you'll receive a password reset link.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-center text-sm text-muted-foreground">
                                    The link will expire in 30 minutes. If you don't see the email, check your spam folder.
                                </p>
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                        setSent(false);
                                        setEmail('');
                                    }}
                                >
                                    Send to a different email
                                </Button>
                                <Button asChild variant="link" className="w-full text-accent">
                                    <Link to="/login">Back to Login</Link>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <div className="hidden lg:block w-1/2 relative">
                    <img
                        src="https://images.unsplash.com/photo-1752649937956-0859c4446377?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                        alt="Artist studio"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />
                </div>
            </div>
        );
    }

    // Request form
    return (
        <div className="min-h-[calc(100vh-64px)] flex" data-testid="forgot-password-page">
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Link>

                    <Card className="border-border/60">
                        <CardHeader className="space-y-1 text-center pb-4">
                            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                                <Mail className="h-8 w-8 text-accent" aria-hidden="true" />
                            </div>
                            <CardTitle id="forgot-password-title" className="font-serif text-2xl">
                                Forgot Password?
                            </CardTitle>
                            <CardDescription>
                                Enter your email and we'll send you a link to reset your password.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4" aria-labelledby="forgot-password-title">
                                {error && (
                                    <div 
                                        className="p-3 rounded-md bg-destructive/10 text-destructive text-sm" 
                                        data-testid="forgot-error"
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label htmlFor="forgot-email" className="sr-only">
                                        Email address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                        <Input
                                            id="forgot-email"
                                            type="email"
                                            placeholder="Enter your email address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className="pl-10 h-12"
                                            data-testid="forgot-email-input"
                                            aria-describedby="forgot-email-hint"
                                            autoComplete="email"
                                        />
                                    </div>
                                    <p id="forgot-email-hint" className="sr-only">
                                        Enter the email address associated with your account
                                    </p>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90"
                                    disabled={loading || !email}
                                    data-testid="forgot-submit-btn"
                                    aria-busy={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        'Send Reset Link'
                                    )}
                                </Button>

                                <p className="text-center text-sm text-muted-foreground pt-2">
                                    Remember your password?{' '}
                                    <Link to="/login" className="text-accent hover:underline">
                                        Back to Login
                                    </Link>
                                </p>

                                <p className="text-center text-xs text-muted-foreground">
                                    Don't have a password yet? Use{' '}
                                    <Link to="/login" className="text-accent hover:underline">
                                        Magic Link
                                    </Link>
                                    {' '}to sign in instead.
                                </p>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <div className="hidden lg:block w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1752649937956-0859c4446377?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Artist studio"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
